import React from 'react';
import { VStack, HStack, Text, Button, Box, View, Skeleton } from 'native-base';

// Assets
import { theme } from '@breakroom-nx/assets';

// Classes & Types
import { UserAvatar, ChatBubble } from '../../';
import { CheckCircleIcon, XCircleIcon } from 'react-native-heroicons/outline';
import { Notification } from '@breakroom-nx/classes';
import { NotificationType, timeFrom } from '@breakroom-nx/helpers';
import { useGetNotificationDataQuery } from '@breakroom-nx/redux';

export interface NotificationActionCellProps {
  notification: Notification;
}

export function NotificationActionCell({
  notification,
}: NotificationActionCellProps) {
  // Load Notification Data
  const { data, isLoading } = useGetNotificationDataQuery(notification);

  return (
    <HStack
      alignItems={'end'}
      space={5}
      rounded={'lg'}
      p={5}
      mb={5}
      bg={theme.colors.surface[200]}
    >
      {isLoading ? (
        <VStack space={2} flex={2} alignItems={'flex-start'}>
          <Skeleton
            h={16}
            w={16}
            rounded="full"
            startColor={theme.colors.surface[400]}
          />
          <Skeleton h={5} rounded="lg" startColor={theme.colors.surface[400]} />
        </VStack>
      ) : (
        <>
          <VStack space={2} flex={2} alignItems={'flex-start'}>
            {data?.room ? (
              <Box>
                <ChatBubble
                  room={data.room}
                  key={data.room.id}
                  disabled={true}
                />
                {data.user && (
                  <View position={'absolute'} right={0} bottom={0}>
                    <UserAvatar
                      uid={data.user.uid}
                      borderColor={'surface.500'}
                      size={8}
                    />
                  </View>
                )}
              </Box>
            ) : (
              data?.user && (
                <UserAvatar uid={data.user.uid} size={60} noBorder />
              )
            )}

            <Text color={'white'} fontWeight={'bold'}>
              {notification.type}
            </Text>

            {notification.type !== NotificationType.System &&
              (data?.room ? (
                <Text>
                  <Text
                    color={theme.colors.white}
                    fontSize={16}
                    fontWeight={600}
                  >
                    {data!.user?.username}
                  </Text>
                  <Text
                    color={theme.colors.surface[600]}
                    fontSize={16}
                    fontWeight={600}
                  >
                    {` ${notification.text.toLowerCase()} `}
                  </Text>
                  <Text
                    color={theme.colors.yellow[200]}
                    fontSize={16}
                    fontWeight={600}
                  >
                    {data.room.title}
                  </Text>
                </Text>
              ) : (
                data?.user && (
                  <Text>
                    <Text
                      color={theme.colors.yellow[200]}
                      fontSize={16}
                      fontWeight={600}
                    >
                      {data!.user?.username}
                    </Text>
                    <Text
                      color={theme.colors.surface[600]}
                      fontSize={16}
                      fontWeight={600}
                    >
                      {` ${notification.text.toLowerCase()}`}
                    </Text>
                  </Text>
                )
              ))}
            <Text
              color={theme.colors.surface[500]}
              fontSize={14}
              fontWeight={400}
            >
              {timeFrom(new Date(notification.timestamp), true)}
            </Text>
          </VStack>
          <VStack space={3}>
            <Button
              rounded={'full'}
              opacity={0.75}
              rightIcon={
                <CheckCircleIcon color={theme.colors.primary[400]} size={20} />
              }
              color={'primary.400'}
              backgroundColor={'rgba(255, 255, 255, 0.07)'}
              _hover={{
                opacity: 1,
              }}
              _pressed={{
                backgroundColor: 'rgba(255, 255, 255, 0.07)',
                opacity: 0.7,
              }}
            >
              <Text color={theme.colors.surface[600]}>{'Accept'}</Text>
            </Button>
            <Button
              rounded={'full'}
              opacity={0.75}
              rightIcon={
                <XCircleIcon color={theme.colors.red[200]} size={20} />
              }
              color={'primary.200'}
              backgroundColor={'rgba(255, 255, 255, 0.07)'}
              _hover={{
                opacity: 1,
              }}
              _pressed={{
                backgroundColor: 'rgba(255, 255, 255, 0.07)',
                opacity: 0.7,
              }}
            >
              <Text color={theme.colors.surface[600]}>{'Decline'}</Text>
            </Button>
          </VStack>
        </>
      )}
    </HStack>
  );
}

export default React.memo(NotificationActionCell);
